import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { User, Settings, Link, Save, CheckCircle2, HardDrive, Mail, Calendar, Cloud, CheckCircle, Trash2, Upload } from 'lucide-react';
import PlantillasEmail from './PlantillasEmail';
import { getProfile, updateProfile, getGmailAuthUrl, getGmailStatus, disconnectGmail, getDriveAuthUrl, getDriveStatus, disconnectDrive, getCalendarAuthUrl, getCalendarStatus, disconnectCalendar, getIntegrationsConfig, saveIntegrationsConfig, uploadUserLogo, getUserLogo, deleteUserLogo } from '../services/apiService';

const Configuracion = ({ vista = 'configuracion', onNavigate }) => {
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  
  // Estado del perfil
  const [nombre, setNombre] = useState('');
  const [email, setEmail] = useState('');
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  
  // Estado de preferencias
  const [idioma, setIdioma] = useState('es');
  const [tema, setTema] = useState('claro');
  
  // Estado de Gmail
  const [gmailConnected, setGmailConnected] = useState(false);
  const [gmailEmail, setGmailEmail] = useState('');
  const [gmailLoading, setGmailLoading] = useState(false);
  
  // Estado de Drive
  const [driveConnected, setDriveConnected] = useState(false);
  const [driveEmail, setDriveEmail] = useState('');
  const [driveLoading, setDriveLoading] = useState(false);
  
  // Estado de Calendar
  const [calendarConnected, setCalendarConnected] = useState(false);
  const [calendarEmail, setCalendarEmail] = useState('');
  const [calendarLoading, setCalendarLoading] = useState(false);
  
  // Estado de Pynn
  const [pynnUrl, setPynnUrl] = useState('');
  const [pynnConfigured, setPynnConfigured] = useState(false);
  const [showPynnConfig, setShowPynnConfig] = useState(false);
  const [pynnLoading, setPynnLoading] = useState(false);
  
  // Función para aplicar el tema
  const aplicarTema = (temaSeleccionado) => {
    if (temaSeleccionado === 'oscuro') {
      document.documentElement.classList.add('dark');
    } else if (temaSeleccionado === 'claro') {
      document.documentElement.classList.remove('dark');
    } else {
      // Sistema: seguir preferencia del sistema
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      if (prefersDark) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
    }
  };

  // Cargar datos del usuario y aplicar tema
  useEffect(() => {
    const loadUsuario = async () => {
      // Cargar preferencias desde localStorage
      const preferencias = JSON.parse(localStorage.getItem('preferencias') || '{}');
      const temaCargado = preferencias.tema || 'claro';
      setIdioma(preferencias.idioma || 'es');
      setTema(temaCargado);
      
      // Cargar datos del usuario desde la API
      try {
        const response = await getProfile();
        if (response.success && response.user) {
          setNombre(response.user.nombre || '');
          setEmail(response.user.email || '');
        }
        
        // Cargar logo del usuario si existe
        try {
          const logoResponse = await getUserLogo();
          if (logoResponse.success && logoResponse.logo) {
            setLogoPreview(logoResponse.logo);
          }
        } catch (logoError) {
          // Si no hay logo, no es un error crítico
          if (logoError.message && !logoError.message.includes('404')) {
            console.warn('No se pudo cargar el logo:', logoError);
          }
        }
      } catch (error) {
        console.error('Error cargando perfil:', error);
        // Fallback a localStorage si la API falla
        const usuarioGuardado = localStorage.getItem('usuario');
        if (usuarioGuardado) {
          try {
            const usuario = JSON.parse(usuarioGuardado);
            setNombre(usuario.nombre || '');
            setEmail(usuario.email || '');
          } catch (e) {
            console.error('Error cargando usuario desde localStorage:', e);
          }
        }
      }
      
      // Aplicar tema al cargar
      aplicarTema(temaCargado);
    };
    
    const loadGmailStatus = async () => {
      try {
        const response = await getGmailStatus();
        if (response.success) {
          setGmailConnected(response.connected);
          setGmailEmail(response.email || '');
        }
      } catch (error) {
        console.error('Error cargando estado de Gmail:', error);
      }
    };
    
    const loadDriveStatus = async () => {
      try {
        const response = await getDriveStatus();
        if (response.success) {
          setDriveConnected(response.connected);
          setDriveEmail(response.email || '');
        }
      } catch (error) {
        console.error('Error cargando estado de Drive:', error);
      }
    };
    
    const loadCalendarStatus = async () => {
      try {
        const response = await getCalendarStatus();
        if (response.success) {
          setCalendarConnected(response.connected);
          setCalendarEmail(response.email || '');
        }
      } catch (error) {
        console.error('Error cargando estado de Calendar:', error);
      }
    };
    
    const loadPynnConfig = async () => {
      try {
        const response = await getIntegrationsConfig();
        if (response.success && response.config && response.config.pynn_url) {
          setPynnUrl(response.config.pynn_url);
          setPynnConfigured(true);
        }
      } catch (error) {
        console.error('Error cargando configuración de Pynn:', error);
      }
    };
    
    loadUsuario();
    loadGmailStatus();
    loadDriveStatus();
    loadCalendarStatus();
    loadPynnConfig();
    
    // Escuchar mensajes del popup de OAuth
    const handleMessage = (event) => {
      if (event.data && event.data.type === 'gmail-connected') {
        setGmailConnected(true);
        setGmailEmail(event.data.email);
        setGmailLoading(false);
        loadGmailStatus();
      }
      if (event.data && event.data.type === 'drive-connected') {
        console.log('✅ Drive conectado desde popup:', event.data.email);
        setDriveConnected(true);
        setDriveEmail(event.data.email);
        setDriveLoading(false);
        loadDriveStatus();
      }
      if (event.data && event.data.type === 'drive-error') {
        console.error('❌ Error conectando Drive:', event.data.error);
        alert(`Error al conectar Drive: ${event.data.error}`);
        setDriveLoading(false);
      }
      if (event.data && event.data.type === 'calendar-connected') {
        console.log('✅ Calendar conectado desde popup:', event.data.email);
        setCalendarConnected(true);
        setCalendarEmail(event.data.email);
        setCalendarLoading(false);
        loadCalendarStatus();
      }
      if (event.data && event.data.type === 'calendar-error') {
        console.error('❌ Error conectando Calendar:', event.data.error);
        alert(`Error al conectar Calendar: ${event.data.error}`);
        setCalendarLoading(false);
      }
    };
    
    window.addEventListener('message', handleMessage);
    
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  // Aplicar tema cuando cambia
  useEffect(() => {
    aplicarTema(tema);
  }, [tema]);

  const handleGuardarPerfil = async () => {
    setLoading(true);
    setSaved(false);
    
    try {
      // Validar que el nombre no esté vacío
      if (!nombre || nombre.trim().length === 0) {
        alert('El nombre es requerido');
        setLoading(false);
        return;
      }
      
      // Actualizar perfil en la base de datos
      const response = await updateProfile({ nombre: nombre.trim() });
      
      if (response.success) {
        // Actualizar también localStorage para compatibilidad
        const usuario = {
          nombre: response.user.nombre,
          email: response.user.email
        };
        localStorage.setItem('usuario', JSON.stringify(usuario));
        
        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
      } else {
        throw new Error(response.error || 'Error al guardar el perfil');
      }
    } catch (error) {
      console.error('Error guardando perfil:', error);
      alert(error.message || 'Error al guardar el perfil');
    } finally {
      setLoading(false);
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validar tipo de archivo
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      alert('Tipo de archivo no permitido. Usa PNG, JPEG, WebP o SVG');
      return;
    }

    // Validar tamaño (máximo 2MB)
    if (file.size > 2 * 1024 * 1024) {
      alert('El archivo es demasiado grande. Máximo 2MB');
      return;
    }

    setLogoFile(file);
    
    // Crear preview
    const reader = new FileReader();
    reader.onload = () => {
      setLogoPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleUploadLogo = async () => {
    if (!logoFile) {
      alert('Por favor, selecciona un archivo primero');
      return;
    }

    setUploadingLogo(true);
    try {
      const response = await uploadUserLogo(logoFile);
      if (response.success) {
        alert('Logo subido exitosamente');
        setLogoFile(null);
        // El logo ya está en logoPreview, no necesitamos recargarlo
        // Disparar evento para actualizar el Header
        window.dispatchEvent(new Event('logoUpdated'));
      } else {
        throw new Error(response.error || 'Error al subir el logo');
      }
    } catch (error) {
      console.error('Error subiendo logo:', error);
      alert(error.message || 'Error al subir el logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleDeleteLogo = async () => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar tu logo personalizado?')) {
      return;
    }

    setUploadingLogo(true);
    try {
      const response = await deleteUserLogo();
      if (response.success) {
        setLogoPreview(null);
        setLogoFile(null);
        alert('Logo eliminado exitosamente');
        // Disparar evento para actualizar el Header
        window.dispatchEvent(new Event('logoUpdated'));
      } else {
        throw new Error(response.error || 'Error al eliminar el logo');
      }
    } catch (error) {
      console.error('Error eliminando logo:', error);
      alert(error.message || 'Error al eliminar el logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleGuardarPreferencias = () => {
    const preferencias = {
      idioma,
      tema
    };
    localStorage.setItem('preferencias', JSON.stringify(preferencias));
    
    // Aplicar tema inmediatamente
    aplicarTema(tema);
    
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const handleConnectGmail = async () => {
    setGmailLoading(true);
    try {
      console.log('🔗 Intentando obtener URL de autorización de Gmail...');
      const response = await getGmailAuthUrl();
      console.log('📥 Respuesta de getGmailAuthUrl:', response);
      
      if (response && response.success && response.authUrl) {
        console.log('✅ URL de autorización obtenida:', response.authUrl);
        // Abrir popup para autorización
        const width = 500;
        const height = 600;
        const left = (window.screen.width - width) / 2;
        const top = (window.screen.height - height) / 2;
        
        const popup = window.open(
          response.authUrl,
          'Gmail Authorization',
          `width=${width},height=${height},left=${left},top=${top}`
        );
        
        // Verificar si el popup fue bloqueado
        if (!popup || popup.closed || typeof popup.closed === 'undefined') {
          alert('Por favor, permite popups para este sitio para conectar Gmail');
          setGmailLoading(false);
          return;
        }
        
        // Monitorear el popup para detectar cuando se cierra
        const checkPopup = setInterval(() => {
          if (popup.closed) {
            clearInterval(checkPopup);
            setGmailLoading(false);
            // Recargar estado de Gmail después de cerrar el popup
            getGmailStatus().then(status => {
              if (status.success && status.connected) {
                setGmailConnected(true);
                setGmailEmail(status.email);
              }
            }).catch(console.error);
          }
        }, 500);
        
        // El popup enviará un mensaje a través de postMessage cuando se complete
      } else {
        const errorMsg = response?.error || 'Error desconocido al obtener URL de autorización';
        console.error('❌ Error en respuesta:', response);
        alert(`Error: ${errorMsg}`);
        setGmailLoading(false);
      }
    } catch (error) {
      console.error('❌ Error conectando Gmail:', error);
      alert(`Error al conectar Gmail: ${error.message || 'Error desconocido'}`);
      setGmailLoading(false);
    }
  };

  const handleDisconnectGmail = async () => {
    if (!window.confirm('¿Estás seguro de que quieres desconectar Gmail?')) {
      return;
    }
    
    setGmailLoading(true);
    try {
      const response = await disconnectGmail();
      if (response.success) {
        setGmailConnected(false);
        setGmailEmail('');
      } else {
        alert('Error al desconectar Gmail');
      }
    } catch (error) {
      console.error('Error desconectando Gmail:', error);
      alert('Error al desconectar Gmail. Inténtalo de nuevo.');
    } finally {
      setGmailLoading(false);
    }
  };

  const handleConnectDrive = async () => {
    setDriveLoading(true);
    try {
      console.log('🔗 Intentando obtener URL de autorización de Drive...');
      const response = await getDriveAuthUrl();
      console.log('📥 Respuesta de getDriveAuthUrl:', response);
      
      if (response && response.success && response.authUrl) {
        console.log('✅ URL de autorización obtenida:', response.authUrl);
        // Abrir popup para autorización
        const width = 500;
        const height = 600;
        const left = (window.screen.width - width) / 2;
        const top = (window.screen.height - height) / 2;
        
        const popup = window.open(
          response.authUrl,
          'Drive Authorization',
          `width=${width},height=${height},left=${left},top=${top}`
        );
        
        // Verificar si el popup fue bloqueado
        if (!popup || popup.closed || typeof popup.closed === 'undefined') {
          alert('Por favor, permite popups para este sitio para conectar Drive');
          setDriveLoading(false);
          return;
        }
        
        // Monitorear el popup para detectar cuando se cierra
        const checkPopup = setInterval(() => {
          if (popup.closed) {
            clearInterval(checkPopup);
            setDriveLoading(false);
            // Recargar estado de Drive después de cerrar el popup
            getDriveStatus().then(status => {
              if (status.success && status.connected) {
                setDriveConnected(true);
                setDriveEmail(status.email);
              }
            }).catch(console.error);
          }
        }, 500);
      } else {
        const errorMsg = response?.error || 'Error desconocido al obtener URL de autorización';
        console.error('❌ Error en respuesta:', response);
        alert(`Error: ${errorMsg}`);
        setDriveLoading(false);
      }
    } catch (error) {
      console.error('❌ Error conectando Drive:', error);
      alert(`Error al conectar Drive: ${error.message || 'Error desconocido'}`);
      setDriveLoading(false);
    }
  };

  const handleDisconnectDrive = async () => {
    if (!window.confirm('¿Estás seguro de que quieres desconectar Drive?')) {
      return;
    }
    
    setDriveLoading(true);
    try {
      const response = await disconnectDrive();
      if (response.success) {
        setDriveConnected(false);
        setDriveEmail('');
      } else {
        alert('Error al desconectar Drive');
      }
    } catch (error) {
      console.error('Error desconectando Drive:', error);
      alert('Error al desconectar Drive. Inténtalo de nuevo.');
    } finally {
      setDriveLoading(false);
    }
  };

  const handleConnectCalendar = async () => {
    setCalendarLoading(true);
    try {
      console.log('🔗 Intentando obtener URL de autorización de Calendar...');
      const response = await getCalendarAuthUrl();
      console.log('📥 Respuesta de getCalendarAuthUrl:', response);
      
      if (response && response.success && response.authUrl) {
        console.log('✅ URL de autorización obtenida:', response.authUrl);
        // Abrir popup para autorización
        const width = 500;
        const height = 600;
        const left = (window.screen.width - width) / 2;
        const top = (window.screen.height - height) / 2;
        
        const popup = window.open(
          response.authUrl,
          'Calendar Authorization',
          `width=${width},height=${height},left=${left},top=${top}`
        );
        
        // Verificar si el popup fue bloqueado
        if (!popup || popup.closed || typeof popup.closed === 'undefined') {
          alert('Por favor, permite popups para este sitio para conectar Calendar');
          setCalendarLoading(false);
          return;
        }
        
        // Monitorear el popup para detectar cuando se cierra
        const checkPopup = setInterval(() => {
          if (popup.closed) {
            clearInterval(checkPopup);
            setCalendarLoading(false);
            // Recargar estado de Calendar después de cerrar el popup
            getCalendarStatus().then(status => {
              if (status.success && status.connected) {
                setCalendarConnected(true);
                setCalendarEmail(status.email);
              }
            }).catch(console.error);
          }
        }, 500);
      } else {
        const errorMsg = response?.error || 'Error desconocido al obtener URL de autorización';
        console.error('❌ Error en respuesta:', response);
        alert(`Error: ${errorMsg}`);
        setCalendarLoading(false);
      }
    } catch (error) {
      console.error('❌ Error conectando Calendar:', error);
      alert(`Error al conectar Calendar: ${error.message || 'Error desconocido'}`);
      setCalendarLoading(false);
    }
  };

  const handleDisconnectCalendar = async () => {
    if (!window.confirm('¿Estás seguro de que quieres desconectar Calendar?')) {
      return;
    }
    
    setCalendarLoading(true);
    try {
      const response = await disconnectCalendar();
      if (response.success) {
        setCalendarConnected(false);
        setCalendarEmail('');
      } else {
        alert('Error al desconectar Calendar');
      }
    } catch (error) {
      console.error('Error desconectando Calendar:', error);
      alert('Error al desconectar Calendar. Inténtalo de nuevo.');
    } finally {
      setCalendarLoading(false);
    }
  };

  const handleConnectPynn = () => {
    setShowPynnConfig(true);
  };

  const handleSavePynnConfig = async () => {
    if (!pynnUrl.trim()) {
      alert('Por favor, ingresa una URL válida');
      return;
    }

    // Validar que sea una URL válida
    try {
      new URL(pynnUrl);
    } catch (e) {
      alert('Por favor, ingresa una URL válida (debe empezar con http:// o https://)');
      return;
    }

    setPynnLoading(true);
    try {
      const response = await saveIntegrationsConfig({ pynn_url: pynnUrl.trim() });
      if (response.success) {
        setPynnConfigured(true);
        setShowPynnConfig(false);
        alert('Configuración de Pynn guardada correctamente');
      } else {
        alert('Error al guardar la configuración');
      }
    } catch (error) {
      console.error('Error guardando configuración de Pynn:', error);
      alert('Error al guardar la configuración. Inténtalo de nuevo.');
    } finally {
      setPynnLoading(false);
    }
  };

  const handleDisconnectPynn = async () => {
    if (!window.confirm('¿Estás seguro de que quieres desconectar Pynn?')) {
      return;
    }
    
    setPynnLoading(true);
    try {
      const response = await saveIntegrationsConfig({ pynn_url: null });
      if (response.success) {
        setPynnConfigured(false);
        setPynnUrl('');
        alert('Pynn desconectado correctamente');
      } else {
        alert('Error al desconectar Pynn');
      }
    } catch (error) {
      console.error('Error desconectando Pynn:', error);
      alert('Error al desconectar Pynn. Inténtalo de nuevo.');
    } finally {
      setPynnLoading(false);
    }
  };

  const vistas = {
    'configuracion': 'Configuración',
    'perfil': 'Perfil',
    'preferencias': 'Preferencias',
    'integraciones': 'Integraciones',
    'plantillas-email': 'Plantillas de Email'
  };

  const nombreVista = vistas[vista] || 'Configuración';

  // Iconos para integraciones
  const integraciones = [
    { id: 'drive', nombre: 'Drive', icon: HardDrive, descripcion: 'Sincronización de documentos' },
    { id: 'gmail', nombre: 'Gmail', icon: Mail, descripcion: 'Sincronización de correos' },
    { id: 'calendar', nombre: 'Calendar', icon: Calendar, descripcion: 'Sincronización de calendario' },
    { id: 'pynn', nombre: 'Pynn', icon: Cloud, descripcion: 'Integración con Pynn' },
    { id: 'orbyn', nombre: 'Orbyn', icon: Cloud, descripcion: 'Integración con Orbyn' }
  ];

  return (
    <>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">{nombreVista}</h1>
          <p className="text-muted-foreground mt-2">
            Gestiona tu perfil y preferencias de la aplicación
          </p>
        </div>

        {vista === 'perfil' && (
          <>
            <Card className="border-border/50 shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5" />
                  Información del Perfil
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Nombre</label>
                  <input
                    type="text"
                    value={nombre}
                    onChange={(e) => setNombre(e.target.value)}
                    className="w-full p-2 rounded-md border border-input bg-background"
                    placeholder="Tu nombre"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full p-2 rounded-md border border-input bg-background"
                    placeholder="tu@email.com"
                    disabled
                  />
                  <p className="text-xs text-muted-foreground">El email no se puede modificar</p>
                </div>
                <div className="flex items-center justify-between pt-4 border-t border-border/50">
                  {saved && (
                    <div className="flex items-center gap-2 text-sm text-green-600">
                      <CheckCircle2 className="h-4 w-4" />
                      Guardado
                    </div>
                  )}
                  <Button 
                    onClick={handleGuardarPerfil} 
                    disabled={loading}
                    className="ml-auto"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    Guardar Cambios
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="border-border/50 shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="h-5 w-5" />
                  Logo Personalizado
                </CardTitle>
                <CardDescription>
                  Sube tu logo personalizado para reemplazar el logo por defecto en el header
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Preview del logo actual */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">Logo Actual</label>
                  <div className="flex items-center gap-4">
                    {logoPreview ? (
                      <div className="flex items-center gap-4">
                        <img 
                          src={logoPreview} 
                          alt="Logo personalizado" 
                          className="h-16 object-contain border border-border rounded p-2 bg-background"
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleDeleteLogo}
                          disabled={uploadingLogo}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Eliminar Logo
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-4">
                        <div className="h-16 w-32 border border-dashed border-border rounded flex items-center justify-center bg-muted/50">
                          <span className="text-xs text-muted-foreground">Sin logo personalizado</span>
                        </div>
                        <p className="text-sm text-muted-foreground">Se usará el logo por defecto</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Selector de archivo */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">Subir Nuevo Logo</label>
                  <div className="flex items-center gap-4">
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
                      onChange={handleLogoChange}
                      className="block w-full text-sm text-muted-foreground
                        file:mr-4 file:py-2 file:px-4
                        file:rounded-md file:border-0
                        file:text-sm file:font-semibold
                        file:bg-primary file:text-primary-foreground
                        file:cursor-pointer
                        hover:file:bg-primary/90"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Formatos permitidos: PNG, JPEG, WebP, SVG. Tamaño máximo: 2MB
                  </p>
                </div>

                {/* Botón de subir */}
                {logoFile && (
                  <div className="flex items-center justify-end gap-2">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setLogoFile(null);
                        // Restaurar preview anterior si existe
                        if (!logoPreview) {
                          // Recargar logo del servidor
                          getUserLogo().then(logoResponse => {
                            if (logoResponse.success && logoResponse.logo) {
                              setLogoPreview(logoResponse.logo);
                            }
                          }).catch(() => {
                            setLogoPreview(null);
                          });
                        }
                      }}
                      disabled={uploadingLogo}
                    >
                      Cancelar
                    </Button>
                    <Button
                      onClick={handleUploadLogo}
                      disabled={uploadingLogo}
                    >
                      {uploadingLogo ? (
                        <>
                          <span className="animate-spin mr-2">⏳</span>
                          Subiendo...
                        </>
                      ) : (
                        <>
                          <Upload className="h-4 w-4 mr-2" />
                          Subir Logo
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}

        {vista === 'preferencias' && (
          <Card className="border-border/50 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Preferencias
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Idioma</label>
                <select 
                  value={idioma} 
                  onChange={(e) => setIdioma(e.target.value)}
                  className="w-full p-2 rounded-md border border-input bg-background"
                  disabled
                >
                  <option value="es">Español</option>
                </select>
                <p className="text-xs text-muted-foreground">Por el momento solo está disponible en español</p>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Tema</label>
                <select 
                  value={tema} 
                  onChange={(e) => setTema(e.target.value)}
                  className="w-full p-2 rounded-md border border-input bg-background"
                >
                  <option value="claro">Claro</option>
                  <option value="oscuro">Oscuro</option>
                  <option value="sistema">Sistema</option>
                </select>
              </div>
              <div className="flex items-center justify-between pt-4 border-t border-border/50">
                {saved && (
                  <div className="flex items-center gap-2 text-sm text-green-600">
                    <CheckCircle2 className="h-4 w-4" />
                    Guardado
                  </div>
                )}
                <Button 
                  onClick={handleGuardarPreferencias} 
                  disabled={loading}
                  className="ml-auto"
                >
                  <Save className="h-4 w-4 mr-2" />
                  Guardar Cambios
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {vista === 'integraciones' && (
          <Card className="border-border/50 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Link className="h-5 w-5" />
                Integraciones
              </CardTitle>
              <CardDescription>
                Conecta con servicios externos para sincronizar tus documentos
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Gmail */}
              <div className="p-4 rounded-lg border border-border/50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Mail className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium">Gmail</p>
                      <p className="text-xs text-muted-foreground">
                        {gmailConnected 
                          ? `Conectado como ${gmailEmail}` 
                          : 'Conecta tu cuenta de Gmail para procesar emails automáticamente'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {gmailConnected && (
                      <CheckCircle className="h-4 w-4 text-green-600" />
                    )}
                    {gmailConnected ? (
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={handleDisconnectGmail}
                        disabled={gmailLoading}
                      >
                        Desconectar
                      </Button>
                    ) : (
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={handleConnectGmail}
                        disabled={gmailLoading}
                      >
                        {gmailLoading ? 'Conectando...' : 'Conectar'}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Drive */}
              <div className="p-4 rounded-lg border border-border/50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <HardDrive className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium">Drive</p>
                      <p className="text-xs text-muted-foreground">
                        {driveConnected 
                          ? `Conectado como ${driveEmail}` 
                          : 'Conecta tu cuenta de Google Drive para navegar documentos'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {driveConnected && (
                      <CheckCircle className="h-4 w-4 text-green-600" />
                    )}
                    {driveConnected ? (
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={handleDisconnectDrive}
                        disabled={driveLoading}
                      >
                        Desconectar
                      </Button>
                    ) : (
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={handleConnectDrive}
                        disabled={driveLoading}
                      >
                        {driveLoading ? 'Conectando...' : 'Conectar'}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Calendar */}
              <div className="p-4 rounded-lg border border-border/50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Calendar className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium">Calendar</p>
                      <p className="text-xs text-muted-foreground">
                        {calendarConnected 
                          ? `Conectado como ${calendarEmail}` 
                          : 'Conecta tu cuenta de Google Calendar para sincronizar eventos'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {calendarConnected && (
                      <CheckCircle className="h-4 w-4 text-green-600" />
                    )}
                    {calendarConnected ? (
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={handleDisconnectCalendar}
                        disabled={calendarLoading}
                      >
                        Desconectar
                      </Button>
                    ) : (
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={handleConnectCalendar}
                        disabled={calendarLoading}
                      >
                        {calendarLoading ? 'Conectando...' : 'Conectar'}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Pynn */}
              <div className="p-4 rounded-lg border border-border/50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Cloud className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium">Pynn</p>
                      <p className="text-xs text-muted-foreground">
                        {pynnConfigured 
                          ? `Configurado: ${pynnUrl}` 
                          : 'Configura la URL de Pynn para gestionar tu dealflow'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {pynnConfigured && (
                      <CheckCircle className="h-4 w-4 text-green-600" />
                    )}
                    {pynnConfigured ? (
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={handleDisconnectPynn}
                        disabled={pynnLoading}
                      >
                        Desconectar
                      </Button>
                    ) : (
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={handleConnectPynn}
                        disabled={pynnLoading}
                      >
                        Configurar
                      </Button>
                    )}
                  </div>
                </div>
                {showPynnConfig && (
                  <div className="mt-4 pt-4 border-t border-border/50 space-y-3">
                    <div className="space-y-2">
                      <label className="text-sm font-medium">URL de Pynn</label>
                      <input
                        type="url"
                        value={pynnUrl}
                        onChange={(e) => setPynnUrl(e.target.value)}
                        placeholder="https://pynn.example.com"
                        className="w-full p-2 rounded-md border border-input bg-background"
                      />
                      <p className="text-xs text-muted-foreground">
                        Ingresa la URL completa de tu instancia de Pynn
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button 
                        onClick={handleSavePynnConfig}
                        disabled={pynnLoading}
                        size="sm"
                      >
                        <Save className="h-4 w-4 mr-2" />
                        Guardar
                      </Button>
                      <Button 
                        variant="outline"
                        onClick={() => {
                          setShowPynnConfig(false);
                          setPynnUrl(pynnConfigured ? pynnUrl : '');
                        }}
                        size="sm"
                        disabled={pynnLoading}
                      >
                        Cancelar
                      </Button>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Otras integraciones */}
              {integraciones.filter(i => i.id !== 'gmail' && i.id !== 'drive' && i.id !== 'calendar' && i.id !== 'pynn').map((integracion) => {
                const Icon = integracion.icon;
                return (
                  <div key={integracion.id} className="p-4 rounded-lg border border-border/50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Icon className="h-5 w-5 text-muted-foreground" />
                        <div>
                          <p className="text-sm font-medium">{integracion.nombre}</p>
                          <p className="text-xs text-muted-foreground">{integracion.descripcion}</p>
                        </div>
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm"
                        disabled
                      >
                        Conectar
                      </Button>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        )}

        {vista === 'plantillas-email' && (
          <PlantillasEmail />
        )}

        {vista === 'configuracion' && (
          <div className="grid gap-4 md:grid-cols-2">
            <Card 
              className="border-border/50 shadow-sm cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => onNavigate && onNavigate('perfil')}
            >
              <CardHeader>
                <CardTitle className="text-base">Perfil</CardTitle>
                <CardDescription className="text-xs">
                  Información personal y cuenta
                </CardDescription>
              </CardHeader>
            </Card>
            <Card 
              className="border-border/50 shadow-sm cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => onNavigate && onNavigate('preferencias')}
            >
              <CardHeader>
                <CardTitle className="text-base">Preferencias</CardTitle>
                <CardDescription className="text-xs">
                  Configuración de la aplicación
                </CardDescription>
              </CardHeader>
            </Card>
            <Card 
              className="border-border/50 shadow-sm cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => onNavigate && onNavigate('integraciones')}
            >
              <CardHeader>
                <CardTitle className="text-base">Integraciones</CardTitle>
                <CardDescription className="text-xs">
                  Servicios externos conectados
                </CardDescription>
              </CardHeader>
            </Card>
            <Card 
              className="border-border/50 shadow-sm cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => onNavigate && onNavigate('plantillas-email')}
            >
              <CardHeader>
                <CardTitle className="text-base">Plantillas de Email</CardTitle>
                <CardDescription className="text-xs">
                  Personaliza los emails a startups
                </CardDescription>
              </CardHeader>
            </Card>
          </div>
        )}
      </div>
    </>
  );
};

export default Configuracion;

